/**
 * V2.1 — app-styled replacements for window.confirm() / window.prompt().
 *
 * Native browser dialogs ("app.groundworkcommand.com says…") are banned
 * app-wide (see eslint.dialogs.config.mjs). These promise-based helpers are
 * drop-in equivalents rendered through the app's Dialog styling:
 *
 *   if (await confirmDialog("Delete this webhook?")) { … }
 *   const note = await promptDialog({ title: "Mark lost", label: "What happened?" });
 *   const pick = await choiceDialog({ message: "…", choices: [{ value: "add", label: "Add" }] });
 *
 * `confirmDialog` resolves true/false; `choiceDialog` resolves the chosen
 * option's `value` (or null) when a gesture has more than two honest outcomes;
 * `promptDialog` resolves the entered
 * string or null on cancel (same contract as window.prompt). Requests are
 * queued FIFO, so sequential awaits (qty → note) behave like the old
 * prompts did. <AppDialogHost /> is mounted once at the app root.
 */
import { useEffect, useState } from "react";
import { Dialog } from "./dialog";
import { Button } from "./button";
import { Input } from "./input";
import { Textarea } from "./textarea";
import { Label } from "./label";

export interface ConfirmOptions {
    message: string;
    title?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    /** Renders the confirm button in the destructive style. */
    destructive?: boolean;
    /**
     * Default-focus the Cancel button instead of Confirm, so Enter/Space
     * backs out. Use for destructive confirms where a reflexive keypress
     * must not fire the action.
     */
    focusCancel?: boolean;
}

export interface PromptOptions {
    title?: string;
    message?: string;
    label?: string;
    placeholder?: string;
    defaultValue?: string;
    /** Disables OK until non-blank input. */
    required?: boolean;
    multiline?: boolean;
    confirmLabel?: string;
}

export interface ChoiceOption {
    /** Value handed back by `choiceDialog` when this button is pressed. */
    value: string;
    label: string;
    /** One line under the button explaining what it does. */
    description?: string;
    variant?: "default" | "outline" | "destructive";
}

export interface ChoiceOptions {
    message: string;
    title?: string;
    /** Rendered top to bottom; the FIRST choice is the default-focused one. */
    choices: ChoiceOption[];
    cancelLabel?: string;
}

type Pending =
    | { kind: "confirm"; opts: ConfirmOptions; resolve: (v: boolean) => void }
    | { kind: "choice"; opts: ChoiceOptions; resolve: (v: string | null) => void }
    | { kind: "prompt"; opts: PromptOptions; resolve: (v: string | null) => void };

let enqueueLive: ((p: Pending) => void) | null = null;
const backlog: Pending[] = [];

function enqueue(p: Pending): void {
    if (enqueueLive) enqueueLive(p);
    else backlog.push(p);
}

export function confirmDialog(options: string | ConfirmOptions): Promise<boolean> {
    const opts = typeof options === "string" ? { message: options } : options;
    return new Promise<boolean>((resolve) => enqueue({ kind: "confirm", opts, resolve }));
}

/**
 * A confirm with more than two ways out. Resolves the chosen option's `value`,
 * or null on cancel/dismiss. Used where a gesture is genuinely ambiguous and
 * guessing would be destructive — e.g. dropping a job on a second tech's lane
 * means either "move it there" or "put them both on it".
 */
export function choiceDialog(options: ChoiceOptions): Promise<string | null> {
    return new Promise<string | null>((resolve) => enqueue({ kind: "choice", opts: options, resolve }));
}

export function promptDialog(options: string | PromptOptions): Promise<string | null> {
    const opts = typeof options === "string" ? { message: options } : options;
    return new Promise<string | null>((resolve) => enqueue({ kind: "prompt", opts, resolve }));
}

export function AppDialogHost() {
    const [queue, setQueue] = useState<Pending[]>([]);
    const [seq, setSeq] = useState(0);

    useEffect(() => {
        enqueueLive = (p) => setQueue((q) => [...q, p]);
        if (backlog.length > 0) {
            const drained = backlog.splice(0, backlog.length);
            setQueue((q) => [...q, ...drained]);
        }
        return () => {
            enqueueLive = null;
        };
    }, []);

    const current = queue[0] ?? null;
    const dismiss = () => {
        setQueue((q) => q.slice(1));
        setSeq((n) => n + 1);
    };
    if (!current) return null;

    if (current.kind === "confirm") {
        const o = current.opts;
        const done = (v: boolean) => {
            current.resolve(v);
            dismiss();
        };
        return (
            <Dialog open title={o.title ?? "Please confirm"} onClose={() => done(false)} className="max-w-sm">
                <div className="space-y-4">
                    <p className="text-sm whitespace-pre-line">{o.message}</p>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" autoFocus={!!o.focusCancel} onClick={() => done(false)}>
                            {o.cancelLabel ?? "Cancel"}
                        </Button>
                        <Button
                            size="sm"
                            variant={o.destructive ? "destructive" : "default"}
                            autoFocus={!o.focusCancel}
                            onClick={() => done(true)}
                        >
                            {o.confirmLabel ?? "OK"}
                        </Button>
                    </div>
                </div>
            </Dialog>
        );
    }

    if (current.kind === "choice") {
        const o = current.opts;
        const done = (v: string | null) => {
            current.resolve(v);
            dismiss();
        };
        return (
            <Dialog open title={o.title ?? "Please choose"} onClose={() => done(null)} className="max-w-sm">
                <div className="space-y-4">
                    <p className="text-sm whitespace-pre-line">{o.message}</p>
                    <div className="space-y-2">
                        {o.choices.map((c, i) => (
                            <Button
                                key={c.value}
                                size="sm"
                                variant={c.variant ?? (i === 0 ? "default" : "outline")}
                                autoFocus={i === 0}
                                className="h-auto w-full flex-col items-start gap-0.5 py-2 text-left"
                                onClick={() => done(c.value)}
                            >
                                <span className="font-medium">{c.label}</span>
                                {c.description && (
                                    <span className="text-[11px] font-normal opacity-80">{c.description}</span>
                                )}
                            </Button>
                        ))}
                    </div>
                    <div className="flex justify-end">
                        <Button variant="ghost" size="sm" onClick={() => done(null)}>
                            {o.cancelLabel ?? "Cancel"}
                        </Button>
                    </div>
                </div>
            </Dialog>
        );
    }

    return <PromptBody key={seq} pending={current} onDone={dismiss} />;
}

function PromptBody({ pending, onDone }: { pending: Extract<Pending, { kind: "prompt" }>; onDone: () => void }) {
    const o = pending.opts;
    const [value, setValue] = useState(o.defaultValue ?? "");
    const done = (v: string | null) => {
        pending.resolve(v);
        onDone();
    };
    const submitDisabled = !!o.required && value.trim().length === 0;
    const submit = () => {
        if (!submitDisabled) done(value);
    };
    return (
        <Dialog open title={o.title ?? o.message ?? "Enter a value"} onClose={() => done(null)} className="max-w-sm">
            <form
                className="space-y-4"
                onSubmit={(e) => {
                    e.preventDefault();
                    submit();
                }}
            >
                {o.title && o.message && <p className="text-sm whitespace-pre-line">{o.message}</p>}
                <div className="space-y-1.5">
                    {o.label && <Label className="text-xs">{o.label}</Label>}
                    {o.multiline ? (
                        <Textarea
                            autoFocus
                            rows={3}
                            placeholder={o.placeholder}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                        />
                    ) : (
                        <Input
                            autoFocus
                            placeholder={o.placeholder}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                        />
                    )}
                </div>
                <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => done(null)}>
                        Cancel
                    </Button>
                    <Button type="submit" size="sm" disabled={submitDisabled}>
                        {o.confirmLabel ?? "OK"}
                    </Button>
                </div>
            </form>
        </Dialog>
    );
}
